/**
 * Axios/HTTP 스타일 에러에서 공통으로 쓰는 판별·추출 (도메인 무관)
 */

type AxiosLikeError = {
  code?: string;
  response?: {
    status?: number;
    data?: { message?: string | string[]; errorKey?: string };
  };
};

function isAxiosLikeError(error: unknown): error is AxiosLikeError {
  return typeof error === 'object' && error !== null;
}

/** Axios 스타일에서 응답 없음·네트워크 코드 → 네트워크 실패 (기존 parseApiError 동작과 동일) */
export function isNetworkError(error: unknown): boolean {
  if (error == null) {
    return false;
  }
  const e = error as AxiosLikeError;
  return (
    e.code === 'NETWORK_ERROR' ||
    e.code === 'ECONNABORTED' ||
    e.response === undefined ||
    e.response === null
  );
}

export function getHttpStatus(error: unknown): number | undefined {
  return isAxiosLikeError(error) ? error.response?.status : undefined;
}

export function getServerMessage(error: unknown): string | undefined {
  if (!isAxiosLikeError(error)) {
    return undefined;
  }
  const raw = error.response?.data?.message;
  if (typeof raw === 'string') {
    return raw;
  }
  if (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === 'string') {
    return raw[0];
  }
  return undefined;
}

/**
 * 백엔드 응답에서 i18n 키 후보 (errorKey 우선, 그다음 message)
 */
export function getBackendI18nKeyFromResponse(error: unknown): string | undefined {
  if (!isAxiosLikeError(error)) {
    return undefined;
  }
  const data = error.response?.data;
  if (!data || typeof data !== 'object') {
    return undefined;
  }
  const d = data as Record<string, unknown>;
  if (typeof d.errorKey === 'string' && d.errorKey.trim()) {
    return d.errorKey;
  }
  const msg = d.message;
  if (typeof msg === 'string' && msg.trim()) {
    return msg;
  }
  if (Array.isArray(msg) && msg.length > 0 && typeof msg[0] === 'string') {
    return msg[0];
  }
  return undefined;
}

/**
 * 백엔드 `auth.email_foo` → 프론트 `auth:email_foo` (i18n 네임스페이스)
 * `ERROR:company:some_key` → `company:some_key` (ERROR 접두사 제거)
 */
export function normalizeBackendI18nKey(raw: string): string {
  let s = raw.trim();
  if (s.startsWith('ERROR:')) {
    s = s.slice('ERROR:'.length);
  }
  if (s.includes(':')) {
    return s;
  }
  const i = s.indexOf('.');
  if (i > 0) {
    return `${s.slice(0, i)}:${s.slice(i + 1)}`;
  }
  return s;
}

/** `domain:key` 또는 `domain.key` 형태면 번역 키로 간주 */
export function looksLikeI18nKey(raw: string): boolean {
  const s = raw.trim();
  if (s.includes(':')) {
    return s.length > 2;
  }
  return /^[a-zA-Z0-9_]+\.[a-zA-Z0-9_.]+$/.test(s);
}
